import { useDispatch, useSelector } from 'react-redux'
import { FormEvent, useEffect, useMemo, useState } from 'react'

import {
    isAlreadyHaveEventWithThatTime,
    isTimeUnavailableByArea,
} from 'features/BookingCalendarController/model/hooks/useCalendarActions'
import {
    AutoCancelingOptions,
    getAvailableAutoCancelingOptions,
    getNearestOption,
    computeExpireTime,
    buildExpireTime,
} from 'features/Booking/model/hooks/expireTimeHelpers'
import { OrderBooking } from 'entities/orders/model'
import { useAreasResourcesQuery } from 'entities/areas/model/hooks/useAreasResourcesQuery'
import { roundNumber } from 'shared/utils/roundNumber'
import { AutocompleteOption } from 'shared/components/Autocomplete'
import {
    addBooking,
    editBooking,
    OrderBookingWithOptionsFields,
    selectCurrentCalendarEvents,
    selectExpireIn,
    setActiveBookingId,
    setCommentToBooking,
    setBookingExpireIn,
    setMembersCountToBooking,
} from '../slices'

export const useBookingFormDateSettings = (
    activeBooking: OrderBooking | null,
) => {
    const dispatch = useDispatch()
    const bookings = useSelector(selectCurrentCalendarEvents)
    const activeBookingId = activeBooking?.id

    const expireInData = useSelector(selectExpireIn)
    const expireInForBooking = activeBookingId
        ? (expireInData?.[activeBooking.id] ?? null)
        : null

    const bookingCreatedAt = activeBooking?.createdAt
    const bookingExpireTime = activeBooking?.expireTime
    const bookingStartTime = activeBooking?.startTime

    const { activeBookingExpireIn, timer } = useMemo(() => {
        const option = getNearestOption(bookingExpireTime, bookingCreatedAt)?.value
        const activeBookingExpireIn = bookingCreatedAt
            ? option ? Number(option) : null
            : expireInForBooking

        const expireTimeModified = buildExpireTime(
            activeBookingExpireIn,
            bookingCreatedAt,
        )?.expireTime
        const isExpireInModified =
            activeBookingExpireIn && bookingCreatedAt && expireTimeModified

        const expireTime = isExpireInModified
            ? new Date(expireTimeModified)
            : bookingExpireTime

        return {
            activeBookingExpireIn,
            timer: computeExpireTime(expireTime),
        }
    }, [expireInForBooking, bookingExpireTime, bookingCreatedAt])

    const onEditMembersCount = (e: FormEvent) => {
        if (activeBooking) {
            const newMembersCount = Math.abs(
                Number((e.target as HTMLInputElement).value),
            )
            if (Number.isNaN(newMembersCount)) {
                return
            }
            dispatch(
                setMembersCountToBooking({
                    bookingId: activeBooking.id,
                    newMembersCount,
                }),
            )
        }
    }

    const onEditComment = (e: FormEvent) => {
        if (activeBooking) {
            const newComment = (e.target as HTMLInputElement).value
            dispatch(
                setCommentToBooking({
                    bookingId: activeBooking.id,
                    newComment,
                }),
            )
        }
    }

    const comment = useMemo(() => activeBooking?.comment, [activeBooking])
    const membersCount = useMemo(
        () => activeBooking?.membersCount,
        [activeBooking],
    )

    const { areas } = useAreasResourcesQuery()

    const areasOptions: AutocompleteOption[] = useMemo(
        () =>
            areas
                ? areas
                      .filter(area => {
                          const areaId = area.id
                          const start = activeBooking?.startTime
                          const end = activeBooking?.endTime
                          if (!start || !end) {
                              return true
                          }

                          const unavailableAreaTime = isTimeUnavailableByArea(
                              start,
                              end,
                              areaId,
                              areas,
                          )

                          if (unavailableAreaTime) {
                              return false
                          }

                          const isAlreadyHaveEvent =
                              isAlreadyHaveEventWithThatTime(
                                  start,
                                  end,
                                  areaId,
                                  bookings,
                                  areas,
                                  activeBooking?.id,
                              )

                          return !isAlreadyHaveEvent
                      })
                      .map(area => ({
                          value: area.id.toString(),
                          label: area.name,
                      }))
                : [],
        [areas, activeBooking],
    )
    const currentAreasOptions = useMemo(
        () =>
            areasOptions.find(
                area => area.value === activeBooking?.area?.id?.toString(),
            ) ?? null,
        [activeBooking, areasOptions],
    )

    const availableAutoCancelingOptions = useMemo(
        () =>
            getAvailableAutoCancelingOptions(bookingStartTime || null),
        [bookingStartTime],
    )

    const defaultAutoCancelingOption = useMemo(() => {
        if (activeBookingExpireIn) {
            return (
                AutoCancelingOptions.find(
                    opt => opt.value === activeBookingExpireIn.toString(),
                ) ?? null
            )
        }

        return bookingCreatedAt
            ? (AutoCancelingOptions.find(opt => opt.value === '0') ?? null)
            : (AutoCancelingOptions.find(opt => opt.value === '-1') ?? null)
    }, [activeBookingId, activeBookingExpireIn, bookingCreatedAt])

    const [currentAutoCancelingOptions, setCurrentAutoCancelingOptions] =
        useState<AutocompleteOption | null>(defaultAutoCancelingOption)

    useEffect(() => {
        setCurrentAutoCancelingOptions(defaultAutoCancelingOption)
    }, [activeBookingId])

    const onUpdateAutoCancelingOption = (option: AutocompleteOption) => {
        const bookingId = activeBooking?.id
        setCurrentAutoCancelingOptions(option)

        if (bookingId) {
            const expireIn = Number(option.value)
            dispatch(setBookingExpireIn({ bookingId, expireIn }))
        }
    }

    const onEditBooking = (
        bookingId: number,
        body: OrderBookingWithOptionsFields,
    ) => {
        dispatch(editBooking({ bookingId, body }))
    }

    const onEditArea = (area: AutocompleteOption) => {
        const areaId = area ? Number(area.value) : null
        const areaName = area ? area.label : null

        if (activeBookingId) {
            onEditBooking(activeBookingId, {
                area: {
                    id: areaId,
                    name: areaName ?? '',
                },
            })
            return
        }

        if (!activeBookingId || !bookings?.length) {
            const bookingId = new Date().getMilliseconds() ** 2
            dispatch(
                addBooking({
                    id: bookingId,
                    areaPrice: 0,
                    comment: '',
                    nomenclatures: [],
                    nomenclaturePrice: 0,
                    nomenclaturePriceWithDiscount: 0,
                    areaPercentDiscount: null,
                    areaAbsolutDiscount: null,
                    areaTotalPrice: 0,
                    areaTotalPriceWithDiscount: 0,
                    totalPrice: 0,
                    totalPriceWithDiscount: 0,
                    membersCount: null,
                    area: {
                        id: areaId,
                        name: areaName ?? '',
                    },
                    startTime: null,
                    endTime: null,
                    createdAt: null,
                    expireTime: null,
                }),
            )
            dispatch(setActiveBookingId({ bookingId }))
            return
        }
    }

    const [areaAbsolutDiscount, setAreaAbsolutDiscount] = useState<
        string | null
    >(activeBooking?.areaAbsolutDiscount?.toString() ?? null)
    const [areaPercentDiscount, setAreaPercentDiscount] = useState<
        string | null
    >(activeBooking?.areaPercentDiscount?.toString() ?? null)

    useEffect(() => {
        if (activeBooking?.areaAbsolutDiscount?.toString() !== areaAbsolutDiscount) {
            setAreaAbsolutDiscount(activeBooking?.areaAbsolutDiscount?.toString() ?? null)
        }
        if (activeBooking?.areaPercentDiscount?.toString() !== areaPercentDiscount) {
            setAreaAbsolutDiscount(activeBooking?.areaPercentDiscount?.toString() ?? null)
        }
    }, [activeBooking])

    const parseDoubleNumber = (value: string | null): number | null => {
        if (!value) return null

        if (value.endsWith('.') || value.endsWith(',')) return null

        const normalized = value.replace(',', '.')
        const num = Number(normalized)

        return Number.isFinite(num) ? Math.abs(num) : null
    }

    const onAbsolutDiscountChange = (e: FormEvent) => {
        if (!activeBooking?.id) {
            return
        }

        const value = (e.target as HTMLInputElement).value
        setAreaAbsolutDiscount(value)
        if (!value) {
            dispatch(
                editBooking({
                    bookingId: activeBooking.id,
                    body: {
                        areaPercentDiscount: null,
                        areaAbsolutDiscount: null,
                    },
                }),
            )
            return
        }

        const newAbsolutDiscount = parseDoubleNumber(value)
        if (!newAbsolutDiscount) {
            return
        }

        const newAreaPercentDiscount = activeBooking.areaPrice
            ? roundNumber((newAbsolutDiscount / activeBooking.areaPrice) * 100)
            : 0
        setAreaPercentDiscount(newAreaPercentDiscount.toString())
        dispatch(
            editBooking({
                bookingId: activeBooking.id,
                body: {
                    areaPercentDiscount: newAreaPercentDiscount,
                    areaAbsolutDiscount: newAbsolutDiscount,
                },
            }),
        )
    }

    const onPercentDiscountChange = (e: FormEvent) => {
        if (!activeBooking) {
            return
        }

        const value = (e.target as HTMLInputElement).value
        setAreaPercentDiscount(value)
        if (!value) {
            dispatch(
                editBooking({
                    bookingId: activeBooking.id,
                    body: {
                        areaPercentDiscount: null,
                        areaAbsolutDiscount: null,
                    },
                }),
            )
            return
        }

        const newPercentDiscount = parseDoubleNumber(value)
        if (!newPercentDiscount) {
            return
        }

        const newAreaAbsolutDiscount = activeBooking.areaPrice
            ? (activeBooking.areaPrice * newPercentDiscount) / 100
            : 0
        setAreaAbsolutDiscount(newAreaAbsolutDiscount.toString())
        dispatch(
            editBooking({
                bookingId: activeBooking.id,
                body: {
                    areaAbsolutDiscount: newAreaAbsolutDiscount,
                    areaPercentDiscount: newPercentDiscount,
                },
            }),
        )
    }

    return {
        timer,
        activeBooking,
        currentAreasOptions,
        areasOptions,
        membersCount,
        onEditMembersCount,
        onEditArea,
        comment,
        onEditComment,
        onAbsolutDiscountChange,
        onPercentDiscountChange,
        defaultAutoCancelingOption,
        currentAutoCancelingOptions,
        onUpdateAutoCancelingOption,
        availableAutoCancelingOptions,

        areaAbsolutDiscount,
        areaPercentDiscount,
    }
}
